// src/components/pages/EventDetails.jsx

import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { EventGetItems } from "../../redux/auth/action/eventDetailAction";
import { getUserProfile } from "../../redux/auth/action/userProfileActions";
import { fetchTicketByEventId } from "../../redux/auth/action/fetchTicketActions";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer";
import { toast, ToastContainer } from "react-toastify";
import { 
  FaTicketAlt, FaCalendarAlt, FaUser, FaBuilding, 
  FaClock, FaMapMarkerAlt, FaImage, FaChevronLeft, FaChevronRight,
  FaApple, FaGoogle, FaMicrosoft, FaEnvelope, FaPhone
} from "react-icons/fa";
import Shimmer from "../../component/Shimmer";
import "./EventDetails.css";
import { handleAddToCalendar } from "../../component/Events/addToCalendar";
import ReactGA from "react-ga4";
import VenueTime from "./VenueTime";
import LocationSection from "./LocationSection";
import TicketSection from "./TicketSection";
import { sanitizeDescription } from "./sanitizeDescription";
import 'react-toastify/dist/ReactToastify.css';
import apiClient from '../../apiClient/apiClient';

// Define defaultImage at the top level
const DEFAULT_IMAGE = "https://via.placeholder.com/300x200.png?text=Default+Image";

// Add this function to fetch organizer details (add this before the EventDetails component)
const fetchOrganizerDetails = async (organizerId) => {
  try {
    const response = await apiClient.get(`api/organizer/${organizerId}/`);
    return response.data;
  } catch (error) {
    console.log('Could not fetch organizer profile:', error);
    return null;
  }
};

const EventDetails = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();
  const accessToken = localStorage.getItem("access") || sessionStorage.getItem("access");

  // Access data from Redux store
  const { loading: eventItemsLoading, data, error: eventError } = useSelector((state) => state.eventItems);
  const { loading: profileLoading, profile, error: profileError } = useSelector((state) => state.profile);
  const { loading: fetchTicketLoading, tickets, error: fetchTicketError } = useSelector((state) => state.fetchTicket);
  const selectedTicket = useSelector((state) => state.selectedTicket.selectedTicket);
  const selectedQuantity = useSelector((state) => state.selectedTicket.ticketQuantity);
  const { is_verified } = profile || {};

  // Refs and state
  const ticketsSectionRef = useRef(null);
  const fetchedOrganizerRef = useRef(null); // Track which organizer we've fetched
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showCalendarOptions, setShowCalendarOptions] = useState(false);
  const [organizerDetails, setOrganizerDetails] = useState(null);

  // Generate array of all event images
  const allImages = data ? 
    [data.thumbnail_path, data.extra_image, data.extra_image2].filter(Boolean) : 
    [];

  const toggleReadMore = () => {
    setIsExpanded(!isExpanded);
  };

  // Auto image slider effect
  useEffect(() => {
    if (allImages.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === allImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000); // Change slide every 5 seconds
    
    return () => clearInterval(interval);
  }, [allImages.length]);

  useEffect(() => {
    if (id) {
      // Reset organizer details when event ID changes
      setOrganizerDetails(null);
      fetchedOrganizerRef.current = null;
      
      dispatch(EventGetItems(id));
      dispatch(fetchTicketByEventId(id));
      
      // Fetch user profile when component loads if user is authenticated
      if (accessToken) {
        dispatch(getUserProfile());
      }
    }
  }, [dispatch, id, accessToken]);

  // Add a new useEffect to fetch organizer details when event data is available
  useEffect(() => {
    const getOrganizerDetails = async () => {
      // Only fetch if we have an organizer ID and haven't fetched this organizer yet
      if (data?.organizer && fetchedOrganizerRef.current !== data.organizer) {
        try {
          const details = await fetchOrganizerDetails(data.organizer);
          if (details) {
            setOrganizerDetails(details);
            fetchedOrganizerRef.current = data.organizer; // Mark this organizer as fetched
          }
        } catch (error) {
          console.error('Error fetching organizer details:', error);
          // Don't update the ref on error so we can retry
        }
      }
    };
    
    getOrganizerDetails();
  }, [data?.organizer]); // Only depend on the organizer ID

  // Handle adding to different calendars
  const handleAddToGoogleCalendar = () => {
    if (!data) return;
    
    const event = {
      title: data.event_name,
      description: data.description ? data.description.replace(/<[^>]*>?/gm, '') : '',
      location: `${data.venue_name || ''}, ${data.venue_address || ''}`,
      startTime: `${data.start_date}T${data.start_time || '00:00:00'}`,
      endTime: `${data.end_date || data.start_date}T${data.end_time || '23:59:59'}`
    };
    
    const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${new Date(event.startTime).toISOString().replace(/-|:|\.\d+/g, '')}/${new Date(event.endTime).toISOString().replace(/-|:|\.\d+/g, '')}&details=${encodeURIComponent(event.description)}&location=${encodeURIComponent(event.location)}`;
    
    window.open(googleUrl, '_blank');
    setShowCalendarOptions(false);
  };
  
  const handleAddToAppleCalendar = () => {
    if (!data) return;
    
    const event = {
      title: data.event_name,
      description: data.description ? data.description.replace(/<[^>]*>?/gm, '') : '',
      location: `${data.venue_name || ''}, ${data.venue_address || ''}`,
      startTime: `${data.start_date}T${data.start_time || '00:00:00'}`,
      endTime: `${data.end_date || data.start_date}T${data.end_time || '23:59:59'}`
    };
    
    // Create .ics file content
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'BEGIN:VEVENT',
      `SUMMARY:${event.title}`,
      `DTSTART:${new Date(event.startTime).toISOString().replace(/-|:|\.\d+/g, '')}`,
      `DTEND:${new Date(event.endTime).toISOString().replace(/-|:|\.\d+/g, '')}`,
      `DESCRIPTION:${event.description}`,
      `LOCATION:${event.location}`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\n');
    
    // Create and download the .ics file
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${event.title.replace(/\s+/g, '_')}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setShowCalendarOptions(false);
  };
  
  const handleAddToOutlookCalendar = () => {
    if (!data) return;
    
    const event = {
      title: data.event_name,
      description: data.description ? data.description.replace(/<[^>]*>?/gm, '') : '',
      location: `${data.venue_name || ''}, ${data.venue_address || ''}`,
      startTime: `${data.start_date}T${data.start_time || '00:00:00'}`,
      endTime: `${data.end_date || data.start_date}T${data.end_time || '23:59:59'}`
    };
    
    const outlookUrl = `https://outlook.office.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(event.title)}&body=${encodeURIComponent(event.description)}&location=${encodeURIComponent(event.location)}&startdt=${encodeURIComponent(new Date(event.startTime).toISOString())}&enddt=${encodeURIComponent(new Date(event.endTime).toISOString())}`;
    
    window.open(outlookUrl, '_blank');
    setShowCalendarOptions(false);
  };

  const handleGetTicketsClick = () => {
    if (fetchTicketLoading || eventItemsLoading) {
      toast.info("Loading event information. Please wait...");
      return;
    }

    if (data?.external_url) {
      window.open(data.external_url, "_blank", "noopener,noreferrer");
      return;
    }

    if (tickets && tickets.length > 0 && !selectedTicket) {
      ticketsSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
      toast.info("Please select a ticket to continue");
      return;
    }

    if (!accessToken) {
      toast.info("Please log in or register before continuing.");
      navigate('/register');
      return;
    }

    // Check if profile is still loading
    if (profileLoading) {
      toast.info("Loading profile information. Please wait...");
      return;
    }

    // Check if profile failed to load
    if (profileError) {
      toast.error("Failed to load profile information. Please try again.");
      return;
    }

    // Check if profile exists but is_verified is undefined
    if (accessToken && profile && typeof is_verified === "undefined") {
      toast.error("Profile verification status unavailable. Please try again.");
      return;
    }

    // Check if user is not verified
    if (accessToken && is_verified === false) {
      toast.error(
        <>
          <p>You must be verified before you can continue.</p>
          <p>Please check your email for a verification link.</p>
        </>
      );
      return;
    }

    // Handle verified user with selected ticket
    if (accessToken && is_verified === true && selectedTicket) {
      if (selectedQuantity > 0) {
        navigate("/readyfortickets");
      } else {
        toast.error("Please select a quantity to proceed.");
      }
    } else if (accessToken && is_verified === true) {
      // Handle verified user without selected ticket (check for free tickets)
      const freeTickets = tickets?.filter(ticket => 
        ticket.ticket_category?.toLowerCase() === "free" || 
        !ticket.price || 
        parseFloat(ticket.price) === 0
      );
      
      if (freeTickets?.length === 1) {
        toast.success("Selected free ticket. Proceeding to checkout...");
        navigate("/readyfortickets");
      } else {
        toast.info("Please select a ticket and quantity to proceed.");
        ticketsSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Navbar />

      <ToastContainer position="top-right" containerId={"eventDetails"} autoClose={3000} />
      
      {/* Hero Section with Auto-sliding Image Carousel */}
      <div className="relative w-full h-[45vh] md:h-[55vh] overflow-hidden">
        {eventItemsLoading ? (
          <Shimmer width="100%" height="100%" />
        ) : (
          <>
            {/* Image Carousel */}
            <div className="h-full w-full">
              {allImages.map((image, index) => (
                <div 
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-1000 ${
                    currentImageIndex === index ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img
                    src={image || DEFAULT_IMAGE}
                    alt={data?.event_name || "Event"}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
            
            {/* Dark overlay for better text visibility */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/60"></div>
            
            {/* Image navigation and indicator dots */}
            {allImages.length > 1 && (
              <>
                <button 
                  onClick={() => setCurrentImageIndex(prev => prev === 0 ? allImages.length - 1 : prev - 1)}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-3 rounded-full focus:outline-none z-10"
                  aria-label="Previous image"
                >
                  <FaChevronLeft />
                </button>
                <button 
                  onClick={() => setCurrentImageIndex(prev => prev === allImages.length - 1 ? 0 : prev + 1)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-3 rounded-full focus:outline-none z-10"
                  aria-label="Next image"
                >
                  <FaChevronRight />
                </button>
                
                {/* Indicator dots */}
                <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
                  {allImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-3 h-3 rounded-full focus:outline-none 
                        ${currentImageIndex === index ? 'bg-white' : 'bg-white/50'}`}
                      aria-label={`Go to image ${index + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
      
      {/* Title, Category, Date and Action Buttons Section */}
      <div className="bg-white shadow-md border-b border-gray-200">
        <div className="container mx-auto max-w-5xl px-4 py-5">
          {/* Title, Category, Date - Positioned exactly where requested */}
          {eventItemsLoading ? (
            <div className="mb-5">
              <Shimmer width="60%" height="36px" borderRadius="rounded" className="mb-2" />
              <Shimmer width="40%" height="24px" borderRadius="rounded" />
            </div>
          ) : (
            <div className="mb-5">
              {/* Category badge */}
              {data?.category_name && (
                <span className="inline-block bg-[#FF8100] text-white px-3 py-1 text-xs font-medium rounded-md mb-2">
                  {data.category_name}
                </span>
              )}
              
              {/* Event Title */}
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {data?.event_name || "Untitled Event"}
              </h1>
              
              {/* Date & Time */}
              <div className="flex items-center text-gray-600">
                <FaCalendarAlt className="mr-2" />
                {data?.start_date && (
                  <span>
                    {new Date(data.start_date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long'
                    })}
                  </span>
                )}
              </div>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              className="bg-[#004B95] hover:bg-[#003b76] transition-colors duration-200 text-white font-medium py-3 px-6 rounded-md flex items-center justify-center"
              onClick={handleGetTicketsClick}
              disabled={eventItemsLoading || fetchTicketLoading}
            >
              <FaTicketAlt className="mr-2" />
              {selectedTicket?.ticket_category?.toLowerCase() === "free" || 
               (selectedTicket && (!selectedTicket.price || parseFloat(selectedTicket.price) === 0))
                ? "Register for Free"
                : selectedTicket 
                  ? `Get Tickets: £${(selectedTicket.price * selectedQuantity).toFixed(2)}`
                  : tickets && tickets.some(t => t.ticket_category?.toLowerCase() === "free" || !t.price || parseFloat(t.price) === 0)
                    ? "Register for Free"
                    : data?.min_ticket_price && parseFloat(data.min_ticket_price) > 0
                      ? `Get Tickets: £${data.min_ticket_price}`
                      : "Get Tickets"
              }
            </button>
            
            {/* Calendar Button with Dropdown */}
            <div className="relative">
              <button
                className="border-2 border-[#004B95] text-[#004B95] hover:bg-gray-50 transition-colors duration-200 font-medium py-3 px-6 rounded-md flex items-center justify-center w-full"
                onClick={() => setShowCalendarOptions(!showCalendarOptions)}
              >
                <FaCalendarAlt className="mr-2" />
                Add to Calendar
              </button>
              
              {/* Calendar Options Dropdown */}
              {showCalendarOptions && (
                <div className="absolute z-10 right-0 mt-2 w-56 bg-white rounded-md shadow-lg overflow-hidden">
                  <div className="py-1">
                    <button
                      onClick={handleAddToGoogleCalendar}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <FaGoogle className="mr-2 text-[#DB4437]" /> Google Calendar
                    </button>
                    <button
                      onClick={handleAddToOutlookCalendar}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <FaMicrosoft className="mr-2 text-[#00A1F1]" /> Outlook Calendar
                    </button>
                    <button
                      onClick={handleAddToAppleCalendar}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <FaApple className="mr-2 text-[#555555]" /> Apple Calendar
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Organizer Info */}
          <div className="mt-4 flex items-center">
            <FaBuilding className="text-[#004B95] mr-2" />
            <div>
              <span className="text-sm text-gray-500">Organized by</span>
              <span className="ml-1 font-medium text-gray-800">
                {organizerDetails?.company_name || "Event Organizer"}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="container mx-auto max-w-5xl px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Event Details */}
          <div className="lg:col-span-2">
            {/* Event Description Section */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <FaUser className="mr-2 text-[#004B95]" />
                About This Event
              </h2>
              
              {/* Key Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 bg-gray-50 p-4 rounded-lg">
                <div className="flex items-start">
                  <div className="bg-[#004B95]/10 p-2 rounded-lg mr-3">
                    <FaClock className="text-[#004B95]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Date and Time</p>
                    <VenueTime eventItemsLoading={eventItemsLoading} data={data} short={false} />
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-[#004B95]/10 p-2 rounded-lg mr-3">
                    <FaMapMarkerAlt className="text-[#004B95]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="font-medium">{data?.venue_name || 'Venue TBA'}</p>
                    <p className="text-sm text-gray-600">{data?.venue_address || 'Address TBA'}</p>
                  </div>
                </div>
              </div>
              
              {/* Description */}
              {eventItemsLoading ? (
                <div className="space-y-4">
                  <Shimmer width="100%" height="16px" borderRadius="rounded" />
                  <Shimmer width="90%" height="16px" borderRadius="rounded" />
                  <Shimmer width="95%" height="16px" borderRadius="rounded" />
                  <Shimmer width="80%" height="16px" borderRadius="rounded" />
                </div>
              ) : (
                <>
                  <div
                    className={`prose max-w-none event-description text-black leading-relaxed ${
                      isExpanded ? "max-h-full" : "max-h-40 overflow-hidden"
                    } md:max-h-full`}
                    dangerouslySetInnerHTML={{ __html: sanitizeDescription(data?.description) }}
                  />
                  <button
                    className="text-[#004B95] hover:underline mt-2 md:hidden"
                    onClick={toggleReadMore}
                  >
                    {isExpanded ? "Read Less" : "Read More"}
                  </button>
                </>
              )}
            </div>
            
            {/* Event Gallery */}
            {!eventItemsLoading && allImages.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                  <FaImage className="mr-2 text-[#004B95]" />
                  Event Gallery
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {allImages.map((image, index) => (
                    <div 
                      key={index} 
                      className="relative group cursor-pointer rounded-lg overflow-hidden"
                      onClick={() => setCurrentImageIndex(index)}
                    >
                      <img
                        src={image}
                        alt={`Event ${index + 1}`}
                        className="w-full h-40 object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Location Map Section */}
            {!eventItemsLoading && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                  <FaMapMarkerAlt className="mr-2 text-[#004B95]" />
                  Event Location
                </h2>
                <LocationSection data={data} />
              </div>
            )}
          </div>
          
          {/* Right Column - Tickets and Organizer Details */}
          <div className="lg:col-span-1">
            {/* Tickets Section */}
            <div ref={ticketsSectionRef} className="bg-white rounded-lg shadow-sm p-6 mb-6 sticky top-4">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <FaTicketAlt className="mr-2 text-[#004B95]" />
                Tickets
              </h2>
              
              {fetchTicketLoading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, index) => (
                    <Shimmer key={index} width="100%" height="80px" borderRadius="rounded-lg" />
                  ))}
                </div>
              ) : data?.event_type === "External" ? (
                <div className="text-center py-6">
                  <p className="text-gray-600 mb-4">This is an external event. Tickets must be purchased from the official website.</p>
                  <a
                    href={data?.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-[#004B95] hover:bg-[#003b76] text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
                  >
                    Visit Official Site
                  </a>
                </div>
              ) : tickets && tickets.length > 0 ? (
                <TicketSection tickets={tickets} />
              ) : (
                <div className="text-center py-6 bg-gray-50 rounded-lg">
                  <p className="text-gray-600">No tickets available for this event.</p>
                </div>
              )}
            </div>
            
            {/* Organizer Info Section */}
            {!eventItemsLoading && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <FaBuilding className="mr-2 text-[#004B95]" size={16} />
                  Organizer Information
                </h3>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center mb-3">
                    <div className="w-12 h-12 bg-white rounded-full overflow-hidden mr-3 flex-shrink-0 border border-gray-200 flex items-center justify-center">
                      {/* Prioritize organizerDetails data over event data */}
                      {organizerDetails?.company_logo ? (
                        <img 
                          src={organizerDetails.company_logo} 
                          alt={`${organizerDetails.company_name || 'Organizer'} logo`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // If image fails to load, show fallback
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                      ) : null}
                      <FaBuilding 
                        className="text-[#004B95] text-lg" 
                        style={{ display: organizerDetails?.company_logo ? 'none' : 'block' }}
                      />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {organizerDetails?.company_name || "Event Organizer"}
                      </h4>
                      <p className="text-sm text-gray-500">Event Organizer</p>
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-700">
                    {organizerDetails?.description || 
                     `${organizerDetails?.company_name || "This organizer"} hosts events. Contact them for more information about this event.`}
                  </p>
                  
                  {/* Contact information if available */}
                  {organizerDetails?.business_email && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center text-sm text-gray-600">
                        <FaEnvelope className="mr-2" />
                        <span>{organizerDetails.business_email}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Add phone number if available */}
                  {organizerDetails?.business_phone && (
                    <div className="mt-2">
                      <div className="flex items-center text-sm text-gray-600">
                        <FaPhone className="mr-2" />
                        <span>{organizerDetails.business_phone}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Add a link to the organizer's profile */}
                  {data?.organizer && (
                    <div className="mt-3">
                      <a 
                        href={`/organizer/${data.organizer}`}
                        className="text-[#004B95] hover:underline text-sm font-medium"
                      >
                        View Organizer Profile →
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default EventDetails;
